import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, ArrowLeft, Check, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_auto-parts-shop-32/artifacts/j4kogefz_download.png";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [mode, setMode] = useState(token ? "reset" : "request"); // "request" or "reset"
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState("");
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token`, {
        params: { token }
      });
      setTokenValid(true);
      setTokenEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
      toast.error(error.response?.data?.detail || "Invalid or expired reset link");
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      toast.success("Check your email for the reset link");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/auth"), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Brendolly's Auto Parts" className="h-20 mx-auto" />
          </Link>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
          {/* Request Reset Form */}
          {mode === "request" && !success && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-white">Forgot Password?</h1>
                <p className="text-zinc-400 mt-2">Enter your email and we'll send you a reset link</p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-zinc-400">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                    required
                    data-testid="reset-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold"
                  data-testid="send-reset-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/auth" className="text-zinc-400 hover:text-white text-sm flex items-center justify-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </>
          )}

          {/* Request Success */}
          {mode === "request" && success && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-zinc-400 mb-6">
                If an account exists for <strong className="text-white">{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <p className="text-zinc-500 text-sm mb-6">
                The link will expire in 1 hour.
              </p>
              <Link to="/auth">
                <Button variant="outline" className="border-zinc-700 text-zinc-300">
                  Return to Login
                </Button>
              </Link>
            </div>
          )}

          {/* Reset Password Form */}
          {mode === "reset" && tokenValid === true && !success && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-white">Reset Password</h1>
                <p className="text-zinc-400 mt-2">Enter your new password for <strong className="text-white">{tokenEmail}</strong></p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-zinc-400">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="text-zinc-400">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold"
                  data-testid="reset-password-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {/* Reset Success */}
          {mode === "reset" && success && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Password Reset!</h2>
              <p className="text-zinc-400 mb-6">
                Your password has been successfully reset. Redirecting you to login...
              </p>
              <Link to="/auth">
                <Button className="bg-primary hover:bg-primary/90">
                  Login Now
                </Button>
              </Link>
            </div>
          )}

          {/* Invalid Token */}
          {mode === "reset" && tokenValid === false && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invalid or Expired Link</h2>
              <p className="text-zinc-400 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Button
                onClick={() => { setMode("request"); setSuccess(false); }}
                className="bg-primary hover:bg-primary/90"
              >
                Request New Link
              </Button>
            </div>
          )}

          {/* Loading Token Verification */}
          {mode === "reset" && tokenValid === null && (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-zinc-400">Verifying reset link...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
